import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  BreakpointObserverService,
  CartDataService,
  ConfigurationService,
  HREF,
  OrderService,
  ProcessedProduct,
  ProductCapabilitiesService,
  ProductProcessingService,
  UserService,
} from '@core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AccessibleClickDirective,
  CartSummaryItemComponent,
  CartTotalInfoComponent,
  CheckoutShippingInformationComponent,
  DIALOG_DEFAULT_CONFIG,
  DialogComponent,
  DialogData,
  DialogResult,
  DigitsGroupingPipe,
} from '@shared';
import { SkeletonTemplateService } from '@skeleton-loader';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { calculateCartTotalPoints, CheckoutAddress, SubmitPayloadId, SubmitPaymentPayload } from '@models';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { CheckoutOrderReviewComponent } from '@order';
import { CheckoutPaymentOptionsComponent } from '../../components/checkout-payment-options/checkout-payment-options.component';
import { AppleCareListingComponent } from '../../../../../../../../libs/ui/apple-components/apple-care-listing/apple-care-listing.component';
import { FormsModule } from '@angular/forms';

enum CheckoutStepEnum {
  Shipping = 0,
  PaymentOptions = 1,
  OrderReview = 2,
}

@Component({
  selector: 'app-checkout-page',
  templateUrl: './checkout-page.component.html',
  styleUrls: ['./checkout-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatInputModule,
    CdkAccordionModule,
    CheckoutOrderReviewComponent,
    CheckoutPaymentOptionsComponent,
    CheckoutShippingInformationComponent,
    CartSummaryItemComponent,
    CartTotalInfoComponent,
    AccessibleClickDirective,
    DigitsGroupingPipe,
    AppleCareListingComponent,
    FormsModule,
  ],
})
export class CheckoutPageComponent implements OnInit {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  private shippingSectionTemplate = viewChild<TemplateRef<unknown>>('shippingSectionTemplate');
  private paymentOptionsTemplate = viewChild<TemplateRef<unknown>>('paymentOptionsTemplate');
  private orderReviewTemplate = viewChild<TemplateRef<unknown>>('orderReviewTemplate');

  public userService = inject(UserService);
  public skeletonService = inject(SkeletonTemplateService);
  private cartDataService = inject(CartDataService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private isMobileView = inject(BreakpointObserverService).isTabletOrLower;
  private orderService = inject(OrderService);
  private capabilitiesService = inject(ProductCapabilitiesService);
  private processingService = inject(ProductProcessingService);
  private dialog = inject(MatDialog);
  private orderReview = viewChild(CheckoutOrderReviewComponent);
  private paymentOptions = viewChild(CheckoutPaymentOptionsComponent);

  isShippingSubmitted = signal(false);
  isPaymentOptionsSubmitted = signal(false);
  isPlaceOrderPending = signal(false);
  private emailOverride = signal<string | null>(null);
  email = computed(() => this.emailOverride() ?? this.userService.emailAddress() ?? '');
  // Entered by the player on the shipping step (prefilled from the casino home address).
  selectedShippingAddress = signal<CheckoutAddress | null>(null);
  cardType = signal<string>('');
  filteredProcessedProducts = signal<ProcessedProduct[]>([]);

  processedProducts = computed<ProcessedProduct[]>(() => {
    const products = this.productCard()?.products ?? [];
    return this.processingService.createProcessedProducts(products);
  });

  // Move toObservable to field initializer (injection context)
  private processedProducts$ = toObservable(this.processedProducts);

  public checkoutSections = computed(() => [
    {
      title: 'Shipping',
      template: this.shippingSectionTemplate(),
      step: CheckoutStepEnum.Shipping,
      editButtonLabel: 'Edit shipping information',
    },
    {
      title: 'Payment options',
      template: this.paymentOptionsTemplate(),
      step: CheckoutStepEnum.PaymentOptions,
      editButtonLabel: 'Edit payment options information',
    },
    {
      title: 'Order review',
      template: this.orderReviewTemplate(),
      step: CheckoutStepEnum.OrderReview,
      editButtonLabel: 'Edit order review information',
    },
  ]);

  public expandedActiveStep = signal(CheckoutStepEnum.Shipping);
  public willBeBilledUsd = signal<number>(null);
  private updatedPointsRedeem = signal<number>(null);

  public pointsRedeem = computed(() => {
    return this.updatedPointsRedeem() !== null
      ? this.updatedPointsRedeem()
      : calculateCartTotalPoints(this.productCard());
  });

  public readonly checkoutStepEnum = CheckoutStepEnum;

  productCard = this.cartDataService.productDetailsCart;
  private alreadyExpanded = new Set();

  ngOnInit(): void {
    this.cartDataService.fetchProductCart();
    this.loadProductCapabilities();
    this.setupAppleCareFiltering();
  }

  setExpandedActiveStep(step: CheckoutStepEnum): void {
    this.expandedActiveStep.set(step);
    this.alreadyExpanded.add(step);

    this.scrollToActiveStep(step);
  }

  onSubmitShippingInformation(address: CheckoutAddress): void {
    this.selectedShippingAddress.set(address);
    this.setExpandedActiveStep(CheckoutStepEnum.PaymentOptions);
    this.isShippingSubmitted.set(true);
  }

  onSubmitPaymentOptions(event: SubmitPaymentPayload): void {
    this.updatedPointsRedeem.set(event.pointsRedeem);
    this.willBeBilledUsd.set(event.amountUsd);
    this.orderService.setPaymentInfo(event); // Ensure payment info is set on submit too
    this.setExpandedActiveStep(CheckoutStepEnum.OrderReview);
    this.isPaymentOptionsSubmitted.set(true);
  }

  onRedeemPointsChange(event: SubmitPaymentPayload): void {
    this.updatedPointsRedeem.set(event.pointsRedeem);
    this.willBeBilledUsd.set(event.amountUsd);
    this.orderService.setPaymentInfo(event);
  }

  onPayerInfoChange(event: SubmitPayloadId): void {
    this.orderService.setPayerInfo(event);
  }

  onSubmitOrderReview(): void {
    if (!this.selectedShippingAddress()) {
      this.setExpandedActiveStep(CheckoutStepEnum.Shipping);
      return;
    }

    this.isPlaceOrderPending.set(true);
    this.orderService
      .placeOrder()
      .pipe(
        tap((response) => {
          response.confirmationNumber = response.number;
          if (response.confirmationNumber && !response.errors?.length) {
            this.cartDataService.fetchProductCart().subscribe();
            this.router.navigate([HREF.CHECKOUT, HREF.CHECKOUT_CONFIRMATION], { state: response });
          } else {
            this.openOrderNotPlacedDialog();
          }
        }),
        // Card declined or casino failed/timed out: the backend rolls back and keeps the cart,
        // so the player stays on checkout with addresses and payment kept and can retry.
        // No cart refetch here: a new cart object resets the payment step (payloadId) and the
        // retry would be sent without payment.
        catchError(() => {
          this.openOrderNotPlacedDialog();
          return of();
        }),
        finalize(() => this.userService.updateUserInfo()),
      )
      .subscribe();
  }

  private openOrderNotPlacedDialog(): void {
    // On success "Place order" stays locked until the confirmation page opens; unlock only for a retry.
    this.isPlaceOrderPending.set(false);
    this.dialog
      .open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
        ...DIALOG_DEFAULT_CONFIG,
        data: {
          title: 'Order not placed',
          message: 'We’re unable to complete your order. Please try again.',
          primaryLabel: 'Try again',
          secondaryLabel: 'Return to Homepage',
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === DialogResult.Secondary) {
          this.router.navigate([HREF.HOME]);
          return;
        }

        // With a card part the player re-enters the card (or another one) on Payment options;
        // a points-only order is retried straight from Order review.
        if (this.willBeBilledUsd() > 0) {
          this.paymentOptions()?.resetPaymentAuthorization();
          this.setExpandedActiveStep(CheckoutStepEnum.PaymentOptions);
          return;
        }

        this.expandedActiveStep.set(CheckoutStepEnum.OrderReview);
        this.orderReview()?.focusPlaceOrder();
      });
  }

  private scrollToActiveStep(step: CheckoutStepEnum): void {
    const accordionTopHeight = 95;
    const mobileMenuExtraHeight = 20;

    window.scrollTo({
      top: (this.isMobileView() ? accordionTopHeight - mobileMenuExtraHeight : accordionTopHeight) * step,
      behavior: 'smooth',
    });
  }

  cardTypeChange(value: string) {
    this.cardType.set(value);
  }

  loadProductCapabilities(): void {
    const cartProducts = this.cartDataService.productDetailsCart().products ?? [];
    this.capabilitiesService.loadProductCapabilities(cartProducts, false); // false for checkout mode
  }
  private setupAppleCareFiltering(): void {
    // Convert the computed signal to an observable and subscribe to changes
    this.processedProducts$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((processed) => {
          if (processed.length > 0) {
            return this.processingService.filterOutAppleCareProducts(processed);
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((filtered) => {
        this.filteredProcessedProducts.set(filtered);
      });
  }

  protected onEmailChange(value: string) {
    this.emailOverride.set(value);
  }
}
